import React, { useEffect, useRef, useState } from 'react';
import {
  CreateOrganizerRequest, Organizer, OrganizerVisibility, countryOfBundesland,
} from '@radevents/shared';
import BundeslandOptions from '../BundeslandOptions';
import { getPlzBundesland } from '../../utils/plzBundesland';
import { BERRY, BORDER, FONT_MONO, INK, LABEL, MUTED } from './organizerSpace';
import MetricHint from './manage/MetricHint';
import { LockIcon } from './manage/chrome';

type VisibilityKey = keyof OrganizerVisibility;

export interface LetterheadRow {
  label: string;
  value?: string | null;
  /** Der Schlüssel des Sichtbarkeits-Schalters; ohne ihn ist das Feld immer öffentlich. */
  field?: VisibilityKey;
  isPublic?: boolean;
  /**
   * Macht die Angabe anklickbar: Website, Karte, `mailto:`, `tel:`.
   *
   * Eine Adresse, die man abtippen muss, um sie zu suchen, ist eine halbe Angabe — und ein
   * Vereinslink, der die Seite verlässt, kostet den Besucher den Weg zurück.
   */
  href?: string;
  /** Zielt der Link nach draußen? Dann neues Tab plus `noopener`. */
  external?: boolean;
  /** Was der Link tut — steht als `title` daran, weil „Hauptstr. 12" das nicht verrät. */
  hint?: string;
}

interface EditBundle {
  /** Der volle Datensatz — der Briefkopf zeigt beim Lesen nur einen Teil davon. */
  profile: Organizer;
  saving: boolean;
  /**
   * Speichert beides in einem Zug: die Angaben (als Vorschlag) und die Sichtbarkeit (sofort).
   * Wirft der Aufruf, bleibt der Bearbeiten-Zustand offen — Eingaben gehen nie verloren.
   */
  onSubmit: (payload: CreateOrganizerRequest, visibility: OrganizerVisibility) => Promise<void>;
}

interface Props {
  rows: LetterheadRow[];
  /** Der Betrachter pflegt diesen Veranstalter: dann steht je Zeile, wer sie sieht. */
  manage?: boolean;
  /** Bearbeiten **auf der Seite**. Ohne diese Prop ist der Briefkopf reine Auskunft. */
  edit?: EditBundle;
}

/**
 * Weltkugel: „steht auf deiner Seite". Gegenstueck zum Schloss aus [LockIcon], deshalb dieselbe
 * Groesse und dieselbe Strichbreite — nebeneinander in einer Spalte muessen beide gleich schwer
 * wirken, sonst liest sich der eine Zustand als der wichtigere.
 */
const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.1"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="8.8" />
    <path d="M3.2 12h17.6" />
    {/* Ein Laengengrad als Ellipse statt zweier: bei 13px werden aus drei Linien ein Fleck. */}
    <path d="M12 3.2c2.6 2.4 4 5.5 4 8.8s-1.4 6.4-4 8.8c-2.6-2.4-4-5.5-4-8.8s1.4-6.4 4-8.8Z" />
  </svg>
);

/** Derselbe Stift wie an Logo und Banner — dieselbe Handlung, dasselbe Zeichen. */
const PencilIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    <path d="M14.5 6.5l3 3" />
  </svg>
);

const CSS = `
/* Die Sichtbarkeit als Zeichen, nicht als Wort (02.09.2026): Weltkugel = steht auf der Seite,
   Schloss = sieht nur die Redaktion. „NUR INTERN" und „ÖFFENTLICH" standen in Versalien-Mono
   sechsmal untereinander am rechten Rand und lasen sich als zweite Spalte mit Inhalt — dabei ist
   die Auskunft je Zeile ein einziges Bit. Die Farbe sagt weiter genau das: Beere ja, grau nein.
   Der Knopf **erklaert** nur (MetricHint) und schaltet nicht: geschaltet wird im
   Bearbeiten-Zustand, wo an derselben Stelle der Kippschalter steht. Zwei Bedienungen an einer
   Position, die je nach Zustand etwas anderes tun, waeren die Falle. */
.lh-mark { display:inline-flex; align-items:center; justify-content:center; flex:0 0 26px;
  width:26px; height:26px; margin-left:4px; padding:0; border:0; border-radius:50%;
  background:none; cursor:pointer; }
.lh-mark:hover { background:rgba(44,44,44,.06); }
.lh-mark:focus-visible { outline:2px solid ${BERRY}; outline-offset:1px; }
.lh-mark--on { color:${BERRY}; }
.lh-mark--off { color:${MUTED}; }
/* Anklickbare Angaben sehen aus wie Text, bis man sie berührt — der Briefkopf soll ein Briefkopf
   bleiben und keine Linksammlung. Erkennbar bleiben sie an der Unterlinie. */
.lh-link { color:${INK}; text-decoration:none; border-bottom:1px solid #D6CFCF; }
.lh-link:hover { color:${BERRY}; border-bottom-color:${BERRY}; }
.lh-link:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; border-radius:3px; }
.lh-ext { font-size:11px; color:${LABEL}; margin-left:5px; }

/* --- Bearbeiten je Zeile -------------------------------------------------------------------
   Der Stift steht **hinter dem Sichtbarkeits-Zeichen in jeder Zeile** und oeffnet den
   Bearbeiten-Zustand mit dem Feld dieser Zeile im Fokus. Er wiederholt sich damit sechsmal — das
   ist der Preis dafuer, dass er dort steht, wo man ihn sucht: an der Angabe, die falsch ist.
   Zwei Anlaeufe davor waren schlechter: als einziges Kind eines space-between-Flex neben der
   Liste wurde er an den aeussersten Rand geschoben (breite Leere daneben, auf dem Phone unter
   der letzten Zeile), und als eigene Kopfzeile („STAMMDATEN" links, Knopf rechts) kostete er
   eine ganze Zeile Hoehe fuer eine Ueberschrift, die niemand braucht. Neben dem Zeichen ist der
   Platz ohnehin da.
   Leise gezeichnet: sechs kraeftige Knoepfe waeren eine zweite Spalte mit Gewicht neben einer
   Auskunft, die je Zeile ein Bit ist. */
.lh-acts { display:flex; align-items:center; gap:2px; flex:0 0 auto; }
.lh-pencil { display:inline-flex; align-items:center; justify-content:center; flex:0 0 26px;
  width:26px; height:26px; padding:0; border:0; border-radius:50%; background:none;
  color:#C0B8B8; cursor:pointer; }
.lh-pencil:hover { background:rgba(44,44,44,.06); color:${BERRY}; }
.lh-pencil:focus-visible { outline:2px solid ${BERRY}; outline-offset:1px; color:${BERRY}; }
/* Ohne eine einzige gefuellte Zeile gibt es keinen Stift — dann steht hier der Weg hinein. */
.lh-start { margin:12px 0 0; }
.vb-letterhead--manage { flex-direction:column; align-items:stretch; }
/* Die Angabenliste bleibt so breit wie ihr Inhalt, obwohl der Briefkopf jetzt eine Spalte ist:
   ueber die volle Breite gezogen fluechteten die Sichtbarkeits-Zeichen an den rechten Rand — die
   Leere, die eben noch neben dem Knopf stand, waere dann zwischen Wert und Zeichen.
   minmax(0,…) statt max-content allein: auf einem schmalen Schirm muss die Spalte unter ihren
   laengsten Wert schrumpfen duerfen, sonst schiebt eine lange E-Mail-Adresse die Seite auf. */
.vb-letterhead--manage .vb-defs { grid-template-columns:max-content minmax(0,max-content); }

/* --- Bearbeiten-Zustand -------------------------------------------------------------------
   Dieselben Zeilen, dieselben Beschriftungen, an derselben Stelle — nur der Wert wird zum Feld
   und die Marke zum Schalter. Ein Dialog daneben hiesse dieselben Angaben in zweiter Gestaltung
   (und genau daran ist der alte Weg gescheitert: der Briefkopf zeigte sie, der Dialog auch). */
.vb-letterhead--edit { flex-direction:column; align-items:stretch; }
/* Drei Spalten statt zwei: die Schalter bekommen eine eigene, damit sie eine Kante bilden und
   die Felder ohne Schalter (Name, „Weitere Angaben") trotzdem an derselben Stelle enden. */
.vb-letterhead--edit .vb-defs { width:100%; grid-template-columns:max-content 1fr max-content;
  gap:11px 18px; align-items:center; }
.vb-letterhead--edit .vb-defs dt { padding-top:0; }
.vb-letterhead--edit .vb-defs dd { min-width:0; }
.lh-field { width:100%; box-sizing:border-box; font-family:inherit; font-size:14px; color:${INK};
  background:#fff; border:1px solid ${BORDER}; border-radius:9px; padding:7px 10px; }
.lh-field::placeholder { color:#B6ADAD; }
.lh-field:focus { outline:none; border-color:${BERRY}; box-shadow:0 0 0 3px rgba(146,44,87,.14); }
/* Mehrteilige Zeilen (Anschrift, Profile) — ein Feld je Zeile, PLZ · Ort · Land nebeneinander. */
.lh-stack { display:grid; gap:8px; min-width:0; }
.lh-geo { display:grid; gap:8px; grid-template-columns:5rem 1fr 1.2fr; }
.lh-group { grid-column:1 / -1; font-family:${FONT_MONO}; font-size:10px; letter-spacing:.1em;
  text-transform:uppercase; color:${LABEL}; border-top:1px solid ${BORDER}; padding:13px 0 0;
  margin:4px 0 0; }
.lh-foot { display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin:16px 0 0; }
.lh-note { font-size:12.5px; color:${MUTED}; margin:0; flex:1 1 200px; }
@media (max-width:560px) {
  .lh-geo { grid-template-columns:1fr; }
}
`;

/**
 * Der Sichtbarkeits-Schalter — ein echter Kipp-Schalter, keine Marke.
 *
 * Vorher stand an dieser Stelle im Dialog eine Pille in genau der Form, die im Briefkopf einen
 * *Zustand* anzeigt („ÖFFENTLICH"). Dass man sie drücken kann, hat niemand gesehen. Jetzt sagen
 * Lage und Farbe zusammen, was gilt: beide Zustände sind gültig, es gibt kein „aus" im Sinne von
 * falsch, deshalb reicht Farbe allein nicht.
 */
const VisibilitySwitch: React.FC<{ on: boolean; what: string; onToggle: () => void }> = ({
  on, what, onToggle,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onToggle}
    className="org-vis text-[10px] font-semibold uppercase tracking-wider"
    style={{ color: on ? BERRY : MUTED }}
    title={on
      ? `${what} steht auf deiner öffentlichen Seite. Klicken, um es zu verbergen.`
      : `${what} sieht nur die Redaktion. Klicken, um es öffentlich zu zeigen.`}
  >
    <span className="org-vis-track" style={{ background: on ? BERRY : BORDER }} aria-hidden>
      <span className="org-vis-knob" style={{ transform: on ? 'translateX(12px)' : 'none' }} />
    </span>
    {on ? 'öffentlich' : 'nur intern'}
  </button>
);

/** Die Felder des Bearbeiten-Zustands, in der Reihenfolge des Briefkopfs. */
type FormState = {
  name: string; contactPerson: string; email: string; phone: string; website: string;
  street: string; postalCode: string; city: string; bundesland: string;
  stravaClubUrl: string; instagramUrl: string; facebookUrl: string; komootUrl: string;
  impressumUrl: string; radNetUrl: string; logoUrl: string;
};

const toForm = (o: Organizer): FormState => ({
  name: o.name ?? '',
  contactPerson: o.contactPerson ?? '',
  email: o.email ?? '',
  phone: o.phone ?? '',
  website: o.website ?? '',
  street: o.street ?? '',
  postalCode: o.postalCode ?? '',
  city: o.city ?? '',
  bundesland: o.bundesland ?? '',
  stravaClubUrl: o.stravaClubUrl ?? '',
  instagramUrl: o.instagramUrl ?? '',
  facebookUrl: o.facebookUrl ?? '',
  komootUrl: o.komootUrl ?? '',
  impressumUrl: o.impressumUrl ?? '',
  radNetUrl: o.radNetUrl ?? '',
  logoUrl: o.logoUrl ?? '',
});

/**
 * Welches Feld der Stift einer Zeile anspringt.
 *
 * Ueber die Beschriftung, weil beide Listen sie fuehren: die Lese-Zeilen kommen als
 * `LetterheadRow` von aussen, die Felder stehen unten als `editRow` mit denselben Woertern.
 * „Region" zeigt auf `bundesland` — im Bearbeiten-Zustand steht es in der Zeile *Anschrift*,
 * gefragt ist aber das Feld, nicht die Zeile. Fehlt ein Eintrag, oeffnet der Stift nur den
 * Bearbeiten-Zustand; das ist der richtige Rueckfall, kein Fehler.
 */
const EDIT_TARGET: Record<string, keyof FormState> = {
  Ansprechpartner: 'contactPerson',
  'E-Mail': 'email',
  Telefon: 'phone',
  Website: 'website',
  Anschrift: 'street',
  Region: 'bundesland',
};

const DEFAULT_VISIBILITY: OrganizerVisibility = {
  publicAddress: true, publicWebsite: true, publicSocial: true,
  publicContactPerson: false, publicEmail: false, publicPhone: false,
};

/**
 * Die Angaben, die den Verein ausmachen — auf einem Briefbogen, nicht in einem Formular.
 *
 * Im Pflege-Zustand steht hinter jeder Zeile, **wer sie sieht**, und dahinter ein Stift; er macht
 * aus derselben Zeile ein Feld und aus derselben Marke einen Schalter — mit dem Feld *dieser*
 * Zeile im Fokus. Bis zum 26.08.2026 lief das über den
 * Dialog `OrganizerFormModal`: dieselben Angaben ein zweites Mal, in anderer Reihenfolge und
 * anderer Gestaltung, mit Feldern, die es im Briefkopf gar nicht gibt. Wer im Briefkopf einen
 * Fehler sah, musste ihn im Dialog erst wiederfinden.
 *
 * **Die Redaktion behält den Dialog** (`Organizers.tsx`, und „neu anlegen" im Event-Formular):
 * sie legt Veranstalter auch an, pflegt `orgKind` und `active` und arbeitet in Listen, nicht auf
 * der Veranstalterseite.
 *
 * **Was mit den Links ist.** Vier Angaben haben im Briefkopf keine Lese-Zeile: die vier Profile
 * (sie stehen als farbige Chips darunter), Impressum und rad-net (nicht öffentlich) und die
 * Logo-Adresse (sie *ist* das Logo oben). Sie tauchen deshalb **nur im Bearbeiten-Zustand** auf —
 * die Profile als eigene Zeile mit dem Schalter, der über sie entscheidet, der Rest unter der
 * Zwischenüberschrift „Weitere Angaben". So bleibt der Briefkopf beim Lesen ein Briefkopf und
 * verliert beim Bearbeiten trotzdem kein Feld.
 */
const OrganizerLetterhead: React.FC<Props> = ({ rows, manage, edit }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [vis, setVis] = useState<OrganizerVisibility>(DEFAULT_VISIBILITY);

  const profile = edit?.profile;

  /**
   * Das Feld, das nach dem Umschalten den Fokus bekommt.
   *
   * Ohne das waere der Stift an der Zeile nur ein zweiter Weg in denselben Zustand — mit ihm ist
   * er der Weg zu *dieser* Angabe, und genau das rechtfertigt, dass er sechsmal dasteht.
   */
  const [focusKey, setFocusKey] = useState<keyof FormState | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const start = (focus?: keyof FormState) => {
    if (!profile) return;
    plzDetectedFor.current = profile.postalCode ?? null;
    setForm(toForm(profile));
    setVis({ ...DEFAULT_VISIBILITY, ...(profile.visibility ?? {}) });
    setFocusKey(focus ?? null);
    setOpen(true);
  };

  // Die PLZ füllt das Bundesland — aber nur für deutsche Adressen. Steht schon ein Land außerhalb
  // Deutschlands im Feld, darf sie nicht zuschlagen, sonst stempelt „20121" (Mailand) „Bayern"
  // über „Italien". Dieselbe Regel wie im Redaktions-Dialog.
  // Je PLZ genau einmal, sonst setzt die Erkennung eine Handauswahl sofort zurueck; die PLZ des
  // gespeicherten Profils gilt beim Oeffnen als erledigt (siehe OrganizerFormModal).
  const plzDetectedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!form || form.postalCode.length !== 5 || plzDetectedFor.current === form.postalCode) return;
    const current = countryOfBundesland(form.bundesland);
    if (current && current !== 'DE') return;
    plzDetectedFor.current = form.postalCode;
    const detected = getPlzBundesland(form.postalCode);
    if (detected && detected !== form.bundesland) setForm(f => (f ? { ...f, bundesland: detected } : f));
  }, [form]);

  const save = async () => {
    if (!form || !profile || !edit) return;
    if (!form.name.trim()) return;
    const t = (v: string) => (v.trim() ? v.trim() : undefined);
    await edit.onSubmit({
      name: form.name.trim(),
      contactPerson: t(form.contactPerson),
      email: t(form.email),
      phone: t(form.phone),
      website: t(form.website),
      street: t(form.street),
      postalCode: t(form.postalCode),
      city: t(form.city),
      bundesland: t(form.bundesland),
      stravaClubUrl: t(form.stravaClubUrl),
      instagramUrl: t(form.instagramUrl),
      facebookUrl: t(form.facebookUrl),
      komootUrl: t(form.komootUrl),
      impressumUrl: t(form.impressumUrl),
      radNetUrl: t(form.radNetUrl),
      logoUrl: t(form.logoUrl),
      // Einordnung (`orgKind`) und „aktiv" stehen hier bewusst nicht: die pflegt die Redaktion,
      // und der Vorschlagsweg fasst sie ohnehin nicht an.
    }, vis);
    setOpen(false);
  };

  const editing = open && !!form && !!edit;

  // Erst nachdem die Felder stehen. Gesucht wird im eigenen Baum, nicht im ganzen Dokument:
  // dieselbe Maske kann auf der Seite ein zweites Mal stehen (Redaktions-Dialog daneben).
  useEffect(() => {
    if (!editing || !focusKey) return;
    rootRef.current?.querySelector<HTMLElement>(`[data-lh-field="${focusKey}"]`)?.focus();
    setFocusKey(null);
  }, [editing, focusKey]);
  const shown = rows.filter(r => !!r.value);
  if (shown.length === 0 && !manage) return null;

  // --- Bausteine des Bearbeiten-Zustands ----------------------------------------------------

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => (f ? { ...f, [k]: e.target.value } : f));

  const field = (k: keyof FormState, placeholder?: string, type = 'text') => (
    <input
      className="lh-field" type={type} value={form ? form[k] : ''} onChange={set(k)}
      placeholder={placeholder} aria-label={placeholder} data-lh-field={k}
    />
  );

  const toggle = (f: VisibilityKey) => () => setVis(v => ({ ...v, [f]: !v[f] }));

  const editRow = (label: string, control: React.ReactNode, f?: VisibilityKey, what?: string) => (
    <React.Fragment key={label}>
      <dt>{label}</dt>
      <dd>{control}</dd>
      {/* Eigene Spalte, auch wenn sie leer bleibt: sonst reicht das Feld einer Zeile ohne
          Schalter weiter nach rechts als das darüber, und die Maske sieht ausgefranst aus. */}
      <dd>{f && <VisibilitySwitch on={!!vis[f]} what={what ?? label} onToggle={toggle(f)} />}</dd>
    </React.Fragment>
  );

  return (
    <div
      ref={rootRef}
      className={`vb-letterhead${edit ? ' vb-letterhead--manage' : ''}${editing ? ' vb-letterhead--edit' : ''}`}
    >
      <style>{CSS}</style>
      <dl className="vb-defs">
        {editing ? (
          <>
            {editRow('Name', field('name', 'Name des Veranstalters'))}
            {editRow('Ansprechpartner', field('contactPerson', 'Vor- und Nachname'),
              'publicContactPerson', 'Der Ansprechpartner')}
            {editRow('E-Mail', field('email', 'verein@example.de', 'email'), 'publicEmail', 'Die E-Mail-Adresse')}
            {editRow('Telefon', field('phone', '0201 123456'), 'publicPhone', 'Die Telefonnummer')}
            {editRow('Website', field('website', 'https://…', 'url'), 'publicWebsite', 'Die Website')}
            {editRow('Anschrift', (
              <div className="lh-stack">
                {field('street', 'Straße und Hausnummer')}
                <div className="lh-geo">
                  {field('postalCode', 'PLZ')}
                  {field('city', 'Ort')}
                  <select className="lh-field" value={form!.bundesland} onChange={set('bundesland')}
                    aria-label="(Bundes)Land" data-lh-field="bundesland">
                    <option value="">— Land wählen —</option>
                    <BundeslandOptions />
                  </select>
                </div>
              </div>
            ), 'publicAddress', 'Die Anschrift')}
            {/* Die Profile haben keine Lese-Zeile — sie stehen als farbige Chips unter dem
                Briefkopf. Hier stehen sie trotzdem, weil der Schalter, der über sie entscheidet,
                sonst neben nichts stünde. */}
            {editRow('Im Netz', (
              <div className="lh-stack">
                {field('instagramUrl', 'Instagram', 'url')}
                {field('facebookUrl', 'Facebook', 'url')}
                {field('stravaClubUrl', 'Strava-Club', 'url')}
                {field('komootUrl', 'Komoot', 'url')}
              </div>
            ), 'publicSocial', 'Deine Profile')}

            <div className="lh-group">Weitere Angaben</div>
            {editRow('Impressum', field('impressumUrl', 'https://…', 'url'))}
            {editRow('rad-net.de', field('radNetUrl', 'https://www.rad-net.de/…', 'url'))}
            {editRow('Logo-Adresse', field('logoUrl', 'https://… (oder oben eins hochladen)', 'url'))}
          </>
        ) : (
          shown.map(row => (
            <React.Fragment key={row.label}>
              <dt>{row.label}</dt>
              <dd style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                {row.href ? (
                  <a
                    className="lh-link"
                    href={row.href}
                    title={row.hint}
                    {...(row.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {row.value}
                    {row.external && <span className="lh-ext" aria-hidden>↗</span>}
                  </a>
                ) : (
                  <span style={{ color: INK }}>{row.value}</span>
                )}
                <div className="lh-acts">
                  {manage && (() => {
                    const isPublic = row.field ? !!row.isPublic : true;
                    // Der Text sagt beides: was der Zustand bedeutet und wo man ihn ändert. Ohne
                    // den zweiten Satz wäre das Zeichen eine Auskunft, aus der nichts folgt.
                    const text = !row.field
                      ? 'Steht immer auf deiner Seite. Name und Region lassen sich nicht abschalten — an ihnen hängen Filter, Suchmaschinen und die Zuordnung deiner Events.'
                      : isPublic
                        ? 'Steht auf deiner öffentlichen Seite. Über den Stift daneben kannst du es verbergen.'
                        : 'Sieht nur die Redaktion, nicht die Besucher deiner Seite. Über den Stift daneben kannst du es öffentlich zeigen.';
                    return (
                      <MetricHint
                        label={isPublic ? 'Öffentlich' : 'Nur intern'}
                        text={text}
                        triggerClassName={`lh-mark ${isPublic ? 'lh-mark--on' : 'lh-mark--off'}`}
                      >
                        {isPublic ? <GlobeIcon /> : <LockIcon />}
                      </MetricHint>
                    );
                  })()}
                  {/* Der Stift springt das Feld *dieser* Zeile an — sonst waere er sechsmal
                      derselbe Knopf. */}
                  {edit && (
                    <button
                      type="button"
                      className="lh-pencil"
                      onClick={() => start(EDIT_TARGET[row.label])}
                      aria-label={`${row.label} bearbeiten`}
                      title={`${row.label} bearbeiten`}
                    >
                      <PencilIcon />
                    </button>
                  )}
                </div>
              </dd>
            </React.Fragment>
          ))
        )}
      </dl>

      {edit && !editing && shown.length === 0 && (
        <div className="lh-start">
          <button className="vb-btn" onClick={() => start()}>Angaben ergänzen</button>
        </div>
      )}

      {edit && editing && (
        <div className="lh-foot">
          {/* Was nach dem Speichern passiert, steht hier und nicht als Absatz über dem Formular:
              der Satz beantwortet eine Frage, die man erst am Knopf stellt. */}
          <p className="lh-note">Name und Region stehen immer auf deiner Seite.</p>
          <button className="vb-btn" onClick={() => setOpen(false)} disabled={edit.saving}>
            Abbrechen
          </button>
          <button className="vb-btn vb-btn--primary" onClick={save}
            disabled={edit.saving || !form?.name.trim()}>
            {edit.saving ? 'Wird gespeichert…' : 'Speichern'}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrganizerLetterhead;
